import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import jwt_decode from "jwt-decode";
import { KeycloakService } from "keycloak-angular";
import { Observable, finalize, from, map, shareReplay, switchMap } from "rxjs";
import { environment } from "../../../environments/environment";
import { User } from "../models/user.model";

@Injectable({
  providedIn: "root",
})
export class UserService {
  /**
   * In-flight /utilisateur request, shared between callers.
   * On first login the /admin route triggers several near-simultaneous calls
   * (FirstLoginService, AdminGuard, AdminComponent): we share a single HTTP request
   * while it is in flight, then release it. cf. CQT-244
   */
  private userFetch$?: Observable<User>;

  constructor(
    private keycloak: KeycloakService,
    private http: HttpClient,
  ) {}

  /**
   * Retrieves the Keycloak token, stores it in localStorage,
   * decodes the JWT, and then fetches the authenticated user
   * from the backend.
   *
   * The request is cached to prevent multiple simultaneous HTTP
   * calls when several components request the current user.
   */
  parseTokenAndFetchUserObservable(): Observable<User> {
    // Create the request only if one is not already in progress.
    if (!this.userFetch$) {
      this.userFetch$ = from(this.keycloak.getToken()).pipe(
        // Retrieve the Keycloak token.
        map((tokenB64) => {
          // Store the token for later use if needed.
          localStorage.setItem("userToken", tokenB64);

          // Decode the JWT (mainly to access its claims).S
          return jwt_decode(tokenB64);
        }),

        // Once the token is available, request the current user
        // from the backend API.
        switchMap(() =>
          this.http.get<User>(`${environment.baseUrl}/utilisateur`),
        ),

        // Clear the cached Observable once the request completes
        // (whether it succeeds or fails), allowing future calls
        // to trigger a new request.
        finalize(() => (this.userFetch$ = undefined)),

        // Share the same HTTP request between all subscribers.
        // - bufferSize: 1 caches the latest emitted value.
        // - refCount: false keeps the shared Observable alive
        //   until the request completes.
        shareReplay({
          bufferSize: 1,
          refCount: false,
        }),
      );
    }

    // Return the cached Observable if a request is already in progress.
    return this.userFetch$;
  }

  getUserFull(): Observable<User> {
    return this.http.get<User>(`${environment.baseUrl}/utilisateur/profile`);
  }

  /**
   * Récupération d'un utilisateur par son ID pour les admins
   */
  getUserById(userId: string): Observable<User> {
    return this.http.get<User>(
      `${environment.baseUrl}/admin/utilisateur/${userId}`,
    );
  }
}
